import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';

export const TEXT_ELEMENTS = ['label', 'div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'time'] as const;

export const TEXT_DEFAULT_ELEMENTS = {
  'display-1': 'h1',
  'heading-1': 'h1',
  'heading-2': 'h2',
  'heading-3': 'h3',
  'heading-4': 'h4',
  'heading-5': 'h5',
  'heading-6': 'h6',
  'body-1': 'p',
  'body-2': 'p',
  'label-1': 'span',
  'label-2': 'span',
  'label-3': 'span',
  /**
   * @TODO [SPE-68] Review “badge‑1”/"badge-2" text style
   * https://linear.app/magicschool/issue/SPE-68/review-badge‑1badge-2-text-style
   */
  'badge-1': 'span',
  'badge-2': 'span',
  'caption-1': 'span',
  'caption-2': 'span',
  eyebrow: 'span',
} as const;

export type TextProps = (React.HTMLAttributes<HTMLElement> | React.TimeHTMLAttributes<HTMLTimeElement>) &
  VariantProps<typeof textVariants> & {
    /** The HTML element to render the text as. If not provided, defaults will be used based on the `variant` prop. */
    element?: (typeof TEXT_ELEMENTS)[number];
    ref?: React.Ref<HTMLElement>;
  };

export const textVariants = cva('', {
  variants: {
    /* The semantic color of the text. */
    color: {
      primary: 'text-primary-text',
      success: 'text-success-text',
      warning: 'text-warning-text',
      error: 'text-error-text',
      info: 'text-info-text',
      default: 'text-default-text',
      'default-strong': 'text-default-text-strong',
      'default-soft': 'text-default-text-soft',
      accent1: 'text-accent-1-text',
      accent2: 'text-accent-2-text',
      inherit: 'text-inherit',
      disabled: 'text-disabled-foreground',
    },
    /** The visual appearance of the text. */
    variant: {
      'display-1': 'text-4xl font-semibold ltr:font-heading rtl:font-rtl [font-feature-settings:"calt"_0]',
      /* Headings pin line-height to 1.5: xl is 1.2 and base/sm/xs are 1.3333, shared with body text. Don't drop the pin. */
      'heading-1': 'text-2xl/[1.5] font-semibold ltr:font-heading rtl:font-rtl [font-feature-settings:"calt"_0]',
      'heading-2': 'text-xl/[1.5] font-semibold ltr:font-heading rtl:font-rtl [font-feature-settings:"calt"_0]',
      'heading-3': 'text-lg/[1.5] font-semibold ltr:font-heading rtl:font-rtl [font-feature-settings:"calt"_0]',
      'heading-4': 'text-base/[1.5] font-semibold ltr:font-heading rtl:font-rtl [font-feature-settings:"calt"_0]',
      'heading-5': 'text-sm/[1.5] font-semibold ltr:font-heading rtl:font-rtl [font-feature-settings:"calt"_0]',
      'heading-6': 'text-xs/[1.5] font-semibold ltr:font-heading rtl:font-rtl [font-feature-settings:"calt"_0]',
      'body-1': 'text-base font-normal',
      'body-2': 'text-sm font-normal',
      'label-1': 'text-sm font-medium',
      'label-2': 'text-sm font-bold',
      'label-3': 'text-xs font-medium',
      'badge-1': 'text-xs font-normal',
      'badge-2': 'text-2xs font-normal',
      'caption-1': 'text-xs font-normal',
      'caption-2': 'text-2xs font-normal',

      eyebrow: 'text-2xs font-semibold leading-tight tracking-widest uppercase',
    },
  },
});

export const Text = forwardRef<HTMLElement, TextProps>(
  ({ children, className, element, color = 'inherit', variant = 'body-2', ...props }, ref) => {
    const Comp = (element || TEXT_DEFAULT_ELEMENTS[variant!]) as React.ElementType;

    return (
      <Comp ref={ref} data-spellbook-name="text" className={cn(textVariants({ color, variant }), className)} {...props}>
        {children}
      </Comp>
    );
  },
);

Text.displayName = 'Text';
